import { t } from "i18next"
import { Card, ComponentState, Divider, Group, Icon, Modal, Skeleton, Text } from "components/common"
import {  Flex, List } from "antd"
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react"
import { localisedValue, capitalizeFirstLetter } from "utils"
import styles from "./index.module.less"
import getAgencyConfigurations from "utils/agencyConfigurations"
import cx from "clsx"
import { useLazyGetInvoiceSummaryQuery } from "./api/index"
import InvoiceSummary from "./invoiceSummary/invoiceSummary"
import CoreSummary from "./coreSummary/coreSummary"
import MediaSummary from "./mediaSummary/mediaSummary"
import InvoicedLeads from "./invoicedLeads/invoicedLeads"
import InvoicedUnits from "./invoicedUnits/invoicedUnits"

const InvoiceModal = forwardRef((_props, ref) => {
  const [tabKey, setTabKey] = useState("summary")
  const [invoiceId, setInvoiceId] = useState()
  const modalRef = useRef()
  const [
    getInvoiceDetails,
    {
      data: invoice,
      isFetching: isFetchingInvoiceDetail,
      isLoading: isLoadingInvoiceDetail,
      error: errorInvoiceDetail,
    },
  ] = useLazyGetInvoiceSummaryQuery()

  useImperativeHandle(
    ref,
    () => ({
      showFor(id) {
        getInvoiceDetails({ id })
        setInvoiceId(id)
        modalRef.current.setVisible(true)
      },
    }),
    []
  )
  const agency = getAgencyConfigurations()
  const tabItems = useMemo(
    () => [
      { tab: t("Summary"), key: "summary", show: true },
      ...(invoice?.billingTypeKey === "tripartite"
        ? [
            {
              tab: `${localisedValue({ object: agency?.Platform, key: "platformTitle" }) || "-"} ${t("Core")}`,
              key: "core",
              show: true,
            },
          ]
        : []),
      {
        tab: (
          <>
            {t("Invoiced Units")} ({invoice?.invoicedUnits?.length || 0})
          </>
        ),
        key: "units",
        show: true,
      },
      {
        tab: `${localisedValue({ object: agency?.Platform, key: "platformTitle" }) || "-"} ${t("Media")}`,
        key: "media",
        show: true,
      },

      ...(invoice?.billingTypeKey === "tripartite" ? [{ tab: t("Invoiced Leads"), key: "leads", show: true }] : []),
    ],
    [invoice]
  )

  const getTabContent = () => ({
    summary: <InvoiceSummary invoice={invoice} />,
    core: <CoreSummary data={invoice?.zameenCore} onUnitsClick={() => handleTabChange("units")} />,
    units: <InvoicedUnits data={invoice?.invoicedUnits}/>,
    media: <MediaSummary data={invoice} onLeadsClick={() => handleTabChange("leads")} />,
    leads: <InvoicedLeads invoiceId={invoiceId} />,
  })

  const handleTabChange = key => {
    setTabKey(key)
  }

  return (
    <Modal
      width={800}
      ref={modalRef}
      onCancel={() => {
        modalRef?.current?.setVisible(false)
        setTabKey("summary")
        setInvoiceId(null)
      }}
      loading={isLoadingInvoiceDetail}
      hideCancelButton={true}
      project
      className={styles.modalBg}
      bodyStyle={{ padding: 24 }}
      footer={null}
      destroyOnClose
      onModalClos
    >
      {!!errorInvoiceDetail ? (
        <ComponentState
          onClick={() => getInvoiceDetails({ id :invoiceId})}
          btnLoading={isFetchingInvoiceDetail}
          description={errorInvoiceDetail?.message}
          status={errorInvoiceDetail?.status}
        />
      ) : isFetchingInvoiceDetail ? (
        <InvoiceSkeleton />
      ) : (
        <>
          <Group template="auto 1fr" gap="24px" className="mbe-32 flexYstart">
            <Icon
              hasBackground
              icon="BsFileEarmarkText"
              color="#35A447"
              size="2.4em"
              iconContainerStyle={{ padding: 19, backgroundColor: "#D3F9DD" }}
            />

            <Group template="auto auto 1fr" gap="24px" className="flexYcenter fs12">
              <Flex gap="4px" vertical>
                <Text color="#000" block>
                  <strong>{invoice?.number}</strong> ({localisedValue({ object: invoice, key: "projectName" })})
                </Text>

                <span>
                  <span className="gray-900">{t("Project Type")}: </span>
                  {capitalizeFirstLetter(localisedValue({ object: invoice, key: "projectType" }))}
                </span>
                <span>
                  <span className="gray-900">{t("Billing Type")}: </span>
                  {capitalizeFirstLetter(localisedValue({ object: invoice, key: "billingTypeKey" }))}
                </span>
              </Flex>
              <Divider type="vertical" style={{ height: 28 }} />
              <Flex gap="4px" vertical>
                <span>
                  <span className="gray-900">{t("Developer")}: </span>
                  {localisedValue({ object: invoice?.projectDeveloper, key: "name" })}
                </span>
                <span>
                  <span className="gray-900">{t("Address")}: </span>
                  {[
                    localisedValue({ object: invoice?.projectDeveloper, key: "streetAddress" }),
                    localisedValue({ object: invoice?.projectDeveloper, key: "city" }),
                    localisedValue({ object: invoice?.projectDeveloper, key: "country" }),
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </span>

                <span>
                  <span className="gray-900">{t("Phone")}: </span>
                  {invoice?.projectDeveloper?.phoneNumbers?.[0]}
                </span>
              </Flex>
            </Group>
          </Group>
          <Card
            tabList={tabItems.filter(item => !!item.show)}
            activeTabKey={tabKey}
            onTabChange={handleTabChange}
            tabBarExtraContent={tabItems.find(item => item.key === tabKey).actionButton}
            className={styles.tabsMain}
            style={{ boxShadow: "none", backgroundColor: "transparent" }}
            bodyStyle={{ padding: "12px 0 0" }}
            bordered={false}
          >
            {getTabContent()[tabKey]}
          </Card>
        </>
      )}
    </Modal>
  )
})

const InvoiceSkeleton = () => {
  return (
    <>
      <Group template="auto 1fr" gap="24px" className="mbe-32">
        <Skeleton type="image" />
        <Skeleton type="text" />
      </Group>
      <List size="small" className={cx(styles.listingMain)}>
        {[1, 2, 3, 4, 5]?.map(e => (
          <List.Item>
            <Skeleton height={20} />
            <Skeleton height={20} />
          </List.Item>
        ))}
      </List>
    </>
  )
}

export default InvoiceModal
